import { useState, useEffect } from 'react';
import { 
  Users, Mail, Calendar, Coffee, LogOut, RefreshCw, 
  CheckCircle, XCircle, Trash2, ChevronDown, ChevronUp,
  MessageSquare, Send, X, Phone, Settings, Image, FileText, Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLoginPage from './AdminLoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tab configuration
const tabs = [
  { id: 'reservations', name: 'Reservations', icon: Calendar },
  { id: 'franchise', name: 'Franchise Interests', icon: Coffee },
  { id: 'contacts', name: 'Contact Messages', icon: MessageSquare },
  { id: 'newsletter', name: 'Newsletter', icon: Mail },
  { id: 'deletion', name: 'Deletion Requests', icon: Trash2 },
];

export default function AdminDashboardPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('reservations');
  const [data, setData] = useState({
    franchise: [],
    reservations: [],
    contacts: [],
    newsletter: [],
    deletion: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [replyModal, setReplyModal] = useState({ open: false, type: '', item: null });
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Newsletter composer state
  const [newsletterTitle, setNewsletterTitle] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterImage, setNewsletterImage] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const authStatus = sessionStorage.getItem('adminAuthenticated');
    const authTime = sessionStorage.getItem('adminAuthTime');
    
    // Session expires after 2 hours
    if (authStatus === 'true' && authTime) {
      const elapsed = Date.now() - parseInt(authTime);
      if (elapsed < 2 * 60 * 60 * 1000) {
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem('adminAuthenticated');
        sessionStorage.removeItem('adminAuthTime');
      }
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [franchiseRes, reservationsRes, contactsRes, newsletterRes, deletionRes] = await Promise.all([
        axios.get(`${API}/franchise-interests`).catch(() => ({ data: [] })),
        axios.get(`${API}/reservations`).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts`).catch(() => ({ data: [] })),
        axios.get(`${API}/newsletter`).catch(() => ({ data: [] })),
        axios.get(`${API}/deletion-requests`).catch(() => ({ data: [] }))
      ]);

      setData({
        franchise: franchiseRes.data || [],
        reservations: reservationsRes.data || [],
        contacts: contactsRes.data || [],
        newsletter: newsletterRes.data || [],
        deletion: deletionRes.data || []
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminAuthTime');
    setIsAuthenticated(false);
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      fetchAllData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-yellow-100 text-yellow-700';
      case 'approved': case 'confirmed': return 'bg-green-100 text-green-700';
      case 'rejected': case 'declined': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-orange-100 text-orange-700';
      case 'completed': case 'replied': return 'bg-green-100 text-green-700';
      default: return 'bg-neutral-100 text-neutral-700';
    }
  };

  const openReplyModal = (type, item) => {
    setReplyModal({ open: true, type, item });
    setReplyMessage('');
  };

  const closeReplyModal = () => {
    setReplyModal({ open: false, type: '', item: null });
    setReplyMessage('');
  };

  const handleConfirmReservation = async () => {
    if (!replyModal.item) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/reservations/${replyModal.item.id}/confirm`, {
        message: replyMessage || null
      });
      alert('Reservation confirmed! Confirmation email sent to customer.');
      closeReplyModal();
      fetchAllData();
    } catch (error) {
      console.error('Error confirming reservation:', error);
      alert('Failed to confirm reservation');
    } finally {
      setSendingReply(false);
    }
  };

  const handleDeclineReservation = async () => {
    if (!replyModal.item || !replyMessage.trim()) {
      alert('Please provide a reason for declining');
      return;
    }
    setSendingReply(true);
    try {
      await axios.post(`${API}/reservations/${replyModal.item.id}/decline`, {
        message: replyMessage
      });
      alert('Reservation declined. Customer has been notified.');
      closeReplyModal();
      fetchAllData();
    } catch (error) {
      console.error('Error declining reservation:', error);
      alert('Failed to decline reservation');
    } finally {
      setSendingReply(false);
    }
  };

  const handleReplyToFranchise = async () => {
    if (!replyModal.item || !replyMessage.trim()) {
      alert('Please enter a message');
      return;
    }
    setSendingReply(true);
    try {
      await axios.post(`${API}/franchise-interests/${replyModal.item.id}/reply`, {
        message: replyMessage
      });
      alert('Reply sent successfully!');
      closeReplyModal();
      fetchAllData();
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleReplyToContact = async () => {
    if (!replyModal.item || !replyMessage.trim()) {
      alert('Please enter a message');
      return;
    }
    setSendingReply(true);
    try {
      await axios.post(`${API}/contacts/${replyModal.item.id}/reply`, {
        message: replyMessage
      });
      alert('Reply sent successfully!');
      closeReplyModal();
      fetchAllData();
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  if (!isAuthenticated) {
    return <AdminLoginPage onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen pt-24 bg-[#FAFAF5]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-black text-white py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-serif text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-neutral-400 text-sm mt-1">Manage enquiries and submissions</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={async () => {
                  if (!window.confirm('This will reset and re-add all 33 drinks to the menu. Continue?')) return;
                  try {
                    const res = await axios.post(`${API}/admin/seed-drinks`);
                    alert(res.data.message);
                  } catch (error) {
                    alert('Error seeding drinks: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="flex items-center gap-2 bg-amber-500 text-black px-4 py-2 text-sm font-medium hover:bg-amber-400 transition-colors"
              >
                <Coffee size={16} />
                Seed Drinks
              </button>
              <Link
                to="/admin/content"
                className="flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
              >
                <Settings size={16} />
                Manage Content
              </Link>
              <button
                onClick={fetchAllData}
                disabled={isLoading}
                className="flex items-center gap-2 bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 transition-colors"
                data-testid="admin-logout-btn"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`p-4 text-left transition-all ${
                activeTab === tab.id ? 'bg-black text-white' : 'bg-white hover:shadow-md'
              }`}
            >
              <tab.icon size={24} className="mb-2" />
              <p className="text-2xl font-bold">{data[tab.id]?.length || 0}</p>
              <p className={`text-xs ${activeTab === tab.id ? 'text-neutral-300' : 'text-neutral-500'}`}>
                {tab.name}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl pb-16">
        <div className="bg-white p-6">
          <h2 className="font-serif text-2xl font-semibold mb-6">
            {tabs.find(t => t.id === activeTab)?.name}
          </h2>

          {isLoading ? (
            <div className="text-center py-12 text-neutral-500">Loading...</div>
          ) : data[activeTab]?.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">No items found</div>
          ) : (
            <div className="space-y-4">
              {/* Reservations */}
              {activeTab === 'reservations' && data.reservations.map((item) => (
                <div key={item.id} className="border border-neutral-200 p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{item.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600">{item.email} · {item.phone || 'No phone'}</p>
                      <p className="text-sm font-medium mt-2 text-black">
                        📅 {item.date} at {item.time} · {item.guests} guests
                        {item.occasion && ` · ${item.occasion}`}
                      </p>
                      {item.message && (
                        <p className="text-sm text-neutral-500 mt-2 italic bg-neutral-50 p-2">"{item.message}"</p>
                      )}
                      <p className="text-xs text-neutral-400 mt-2">{formatDate(item.created_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      {item.status === 'pending' && (
                        <button
                          onClick={() => openReplyModal('reservation', item)}
                          className="flex items-center gap-1 text-sm bg-green-600 text-white px-4 py-2 hover:bg-green-700"
                        >
                          <Send size={14} /> Respond
                        </button>
                      )}
                      <button
                        onClick={() => deleteItem('reservations', item.id)}
                        className="p-2 bg-red-100 text-red-700 hover:bg-red-200"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Franchise Interests */}
              {activeTab === 'franchise' && data.franchise.map((item) => (
                <div key={item.id} className="border border-neutral-200 p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{item.firstName} {item.lastName}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600">{item.email} · {item.phone}</p>
                      <p className="text-sm text-neutral-500">📍 Location: {item.location}</p>
                      <p className="text-xs text-neutral-400 mt-2">{formatDate(item.created_at)}</p>
                    </div>
                    <button onClick={() => toggleExpand(item.id)} className="p-2">
                      {expandedItems[item.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                  
                  {expandedItems[item.id] && (
                    <div className="mt-4 pt-4 border-t border-neutral-200">
                      {item.message && (
                        <p className="text-sm text-neutral-600 mb-4 bg-neutral-50 p-3">
                          <strong>Their Message:</strong> "{item.message}"
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => openReplyModal('franchise', item)}
                          className="flex items-center gap-1 text-sm bg-black text-white px-4 py-2 hover:bg-neutral-800"
                        >
                          <Send size={14} /> Reply via Email
                        </button>
                        <a
                          href={`tel:${item.phone}`}
                          className="flex items-center gap-1 text-sm bg-green-100 text-green-700 px-4 py-2 hover:bg-green-200"
                        >
                          <Phone size={14} /> Call
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Contacts */}
              {activeTab === 'contacts' && data.contacts.map((item) => (
                <div key={item.id} className="border border-neutral-200 p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{item.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600">{item.email} {item.phone && `· ${item.phone}`}</p>
                      <p className="text-sm font-medium mt-2">Subject: {item.subject}</p>
                      <p className="text-sm text-neutral-600 mt-2 bg-neutral-50 p-3">{item.message}</p>
                      <p className="text-xs text-neutral-400 mt-2">{formatDate(item.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-100">
                    <button
                      onClick={() => openReplyModal('contact', item)}
                      className="flex items-center gap-1 text-sm bg-black text-white px-4 py-2 hover:bg-neutral-800"
                    >
                      <Send size={14} /> Reply via Email
                    </button>
                    <button
                      onClick={() => deleteItem('contacts', item.id)}
                      className="flex items-center gap-1 text-sm bg-red-100 text-red-700 px-3 py-2 hover:bg-red-200"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}

              {/* Newsletter */}
              {activeTab === 'newsletter' && (
                <div className="space-y-8">
                  {/* Newsletter Composer */}
                  <div className="border border-neutral-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <FileText size={20} />
                      <h3 className="font-semibold text-lg">Compose Newsletter</h3>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Newsletter Title *
                        </label>
                        <input
                          type="text"
                          value={newsletterTitle}
                          onChange={(e) => setNewsletterTitle(e.target.value)}
                          placeholder="e.g., Spring Menu Launch, Special Holiday Hours..."
                          className="w-full border border-neutral-200 px-4 py-3 focus:border-black focus:outline-none"
                          maxLength={100}
                        />
                        <p className="text-xs text-neutral-400 mt-1">{newsletterTitle.length}/100 characters</p>
                      </div>
                      
                      {/* Message */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Message *
                        </label>
                        <textarea
                          value={newsletterMessage}
                          onChange={(e) => {
                            const words = e.target.value.split(/\s+/).filter(w => w.length > 0);
                            if (words.length <= 1000) {
                              setNewsletterMessage(e.target.value);
                            }
                          }}
                          placeholder="Write your newsletter content here..."
                          rows={10}
                          className="w-full border border-neutral-200 px-4 py-3 focus:border-black focus:outline-none resize-none"
                        />
                        <p className="text-xs text-neutral-400 mt-1">
                          {newsletterMessage.split(/\s+/).filter(w => w.length > 0).length}/1000 words
                        </p>
                      </div>
                      
                      {/* Image URL (Optional) */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          <Image size={16} className="inline mr-1" />
                          Image URL (Optional)
                        </label>
                        <input
                          type="url"
                          value={newsletterImage}
                          onChange={(e) => setNewsletterImage(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="w-full border border-neutral-200 px-4 py-3 focus:border-black focus:outline-none"
                        />
                        <p className="text-xs text-neutral-400 mt-1">
                          Paste a direct link to an image (JPG, PNG). Use a service like Imgur or your website.
                        </p>
                        {newsletterImage && (
                          <div className="mt-2">
                            <img 
                              src={newsletterImage} 
                              alt="Preview" 
                              className="max-w-xs h-auto border"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => setShowPreview(true)}
                          disabled={!newsletterTitle || !newsletterMessage}
                          className="flex items-center gap-2 px-6 py-3 border border-black text-black hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Preview
                        </button>
                        <button
                          onClick={async () => {
                            if (!newsletterTitle || !newsletterMessage) return;
                            
                            setSendingNewsletter(true);
                            setNewsletterSuccess(null);
                            
                            try {
                              const res = await axios.post(`${API}/newsletter/send`, {
                                title: newsletterTitle,
                                message: newsletterMessage,
                                image_url: newsletterImage || null
                              });
                              
                              setNewsletterSuccess(`✅ ${res.data.message}`);
                              setNewsletterTitle('');
                              setNewsletterMessage('');
                              setNewsletterImage('');
                            } catch (error) {
                              setNewsletterSuccess(`❌ Error: ${error.response?.data?.detail || 'Failed to send'}`);
                            } finally {
                              setSendingNewsletter(false);
                            }
                          }}
                          disabled={!newsletterTitle || !newsletterMessage || sendingNewsletter}
                          className="flex items-center gap-2 px-6 py-3 bg-black text-white hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {sendingNewsletter ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send size={16} />
                              Send to {data.newsletter.length} Subscribers
                            </>
                          )}
                        </button>
                      </div>
                      
                      {newsletterSuccess && (
                        <p className={`mt-4 p-3 ${newsletterSuccess.startsWith('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                          {newsletterSuccess}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Subscribers Summary (Privacy Protected) */}
                  <div className="border border-neutral-200 p-6 bg-neutral-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">Subscribers</h3>
                        <p className="text-3xl font-bold mt-2">{data.newsletter.length}</p>
                        <p className="text-sm text-neutral-500 mt-1">Total email subscribers</p>
                      </div>
                      <div className="text-right">
                        <Mail size={48} className="text-neutral-300" />
                      </div>
                    </div>
                    <p className="text-xs text-neutral-400 mt-4 border-t pt-4">
                      Subscriber emails are stored securely in the database and not displayed here for privacy. 
                      Use the "Send Newsletter" button above to reach all subscribers.
                    </p>
                  </div>
                </div>
              )}

              {/* Deletion Requests */}
              {activeTab === 'deletion' && data.deletion.map((item) => (
                <div key={item.id} className="border border-neutral-200 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{item.email}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600">Request Type: {item.request_type}</p>
                      {item.message && (
                        <p className="text-sm text-neutral-500 mt-2">"{item.message}"</p>
                      )}
                      <p className="text-xs text-neutral-400 mt-2">{formatDate(item.created_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          await axios.patch(`${API}/deletion-requests/${item.id}/status`, { status: 'completed' });
                          fetchAllData();
                        }}
                        className="p-2 bg-green-100 text-green-700 hover:bg-green-200"
                        title="Mark Completed"
                      >
                        <CheckCircle size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reply Modal */}
      {replyModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-serif text-xl font-semibold">
                {replyModal.type === 'reservation' ? 'Respond to Reservation' : 
                 replyModal.type === 'franchise' ? 'Reply to Franchise Enquiry' :
                 'Reply to Message'}
              </h3>
              <button onClick={closeReplyModal} className="p-2 hover:bg-neutral-100 rounded">
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              {/* Customer Info */}
              <div className="bg-neutral-50 p-4 mb-4">
                {replyModal.type === 'reservation' && replyModal.item && (
                  <>
                    <p className="font-semibold">{replyModal.item.name}</p>
                    <p className="text-sm text-neutral-600">{replyModal.item.email}</p>
                    <p className="text-sm font-medium mt-2">
                      📅 {replyModal.item.date} at {replyModal.item.time} · {replyModal.item.guests} guests
                    </p>
                  </>
                )}
                {replyModal.type === 'franchise' && replyModal.item && (
                  <>
                    <p className="font-semibold">{replyModal.item.firstName} {replyModal.item.lastName}</p>
                    <p className="text-sm text-neutral-600">{replyModal.item.email}</p>
                    <p className="text-sm text-neutral-500">Location: {replyModal.item.location}</p>
                  </>
                )}
                {replyModal.type === 'contact' && replyModal.item && (
                  <>
                    <p className="font-semibold">{replyModal.item.name}</p>
                    <p className="text-sm text-neutral-600">{replyModal.item.email}</p>
                    <p className="text-sm font-medium mt-1">Re: {replyModal.item.subject}</p>
                  </>
                )}
              </div>

              {/* Reply Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {replyModal.type === 'reservation' 
                    ? 'Additional Message (optional for confirm, required for decline)' 
                    : 'Your Reply Message'}
                </label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={5}
                  className="w-full border border-neutral-300 p-3 text-sm focus:outline-none focus:border-black"
                  placeholder={
                    replyModal.type === 'reservation' 
                      ? "Add any special notes or instructions for the customer..." 
                      : "Type your reply here..."
                  }
                />
              </div>

              {/* Reservation Notice */}
              {replyModal.type === 'reservation' && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 mb-4 text-sm">
                  <p className="font-medium text-yellow-800">📞 Call-to-Confirm Policy</p>
                  <p className="text-yellow-700 mt-1">
                    The confirmation email will include a reminder that the customer MUST call on the day of booking to confirm attendance.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                {replyModal.type === 'reservation' ? (
                  <>
                    <button
                      onClick={handleConfirmReservation}
                      disabled={sendingReply}
                      className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle size={18} />
                      {sendingReply ? 'Sending...' : 'Confirm & Send Email'}
                    </button>
                    <button
                      onClick={handleDeclineReservation}
                      disabled={sendingReply}
                      className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-3 font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      <XCircle size={18} />
                      Decline
                    </button>
                  </>
                ) : replyModal.type === 'franchise' ? (
                  <button
                    onClick={handleReplyToFranchise}
                    disabled={sendingReply || !replyMessage.trim()}
                    className="w-full flex items-center justify-center gap-2 bg-black text-white py-3 font-medium hover:bg-neutral-800 disabled:opacity-50"
                  >
                    <Send size={18} />
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                ) : (
                  <button
                    onClick={handleReplyToContact}
                    disabled={sendingReply || !replyMessage.trim()}
                    className="w-full flex items-center justify-center gap-2 bg-black text-white py-3 font-medium hover:bg-neutral-800 disabled:opacity-50"
                  >
                    <Send size={18} />
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h3 className="font-serif text-xl font-semibold">Newsletter Preview</h3>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-neutral-100">
                <X size={20} />
              </button>
            </div>
            
            {/* Email Preview */}
            <div className="bg-[#FAFAF5] p-6">
              <div className="max-w-lg mx-auto bg-white border border-neutral-200">
                {/* Header */}
                <div className="bg-black text-white p-8 text-center">
                  <h1 className="text-2xl tracking-wider font-serif">HONEY I'M HOME</h1>
                  <p className="text-neutral-400 text-sm italic mt-1">Coffee</p>
                </div>
                
                {/* Title */}
                <div className="p-8 text-center">
                  <h2 className="font-serif text-2xl italic">{newsletterTitle || 'Newsletter Title'}</h2>
                </div>
                
                {/* Image */}
                {newsletterImage && (
                  <div className="px-8 pb-4">
                    <img 
                      src={newsletterImage} 
                      alt="Newsletter" 
                      className="w-full h-auto rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                
                {/* Message */}
                <div className="px-8 pb-8">
                  <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">
                    {newsletterMessage || 'Your message will appear here...'}
                  </p>
                </div>
                
                {/* CTA */}
                <div className="px-8 pb-8 text-center">
                  <span className="inline-block bg-black text-white px-8 py-3 text-xs tracking-widest uppercase font-bold">
                    Visit Us
                  </span>
                </div>
                
                {/* Footer */}
                <div className="border-t p-6 text-center text-xs text-neutral-500">
                  <p>451 High Road, Woodford Green, London IG8 0XE</p>
                  <p className="mt-1">+44 7739 083131 · Enquiries@honeyimhomecoffee.com</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setShowPreview(false)}
                className="flex-1 py-3 border border-neutral-200 hover:bg-neutral-50"
              >
                Edit
              </button>
              <button
                onClick={async () => {
                  setShowPreview(false);
                  setSendingNewsletter(true);
                  setNewsletterSuccess(null);
                  
                  try {
                    const res = await axios.post(`${API}/newsletter/send`, {
                      title: newsletterTitle,
                      message: newsletterMessage,
                      image_url: newsletterImage || null
                    });
                    
                    setNewsletterSuccess(`✅ ${res.data.message}`);
                    setNewsletterTitle('');
                    setNewsletterMessage('');
                    setNewsletterImage('');
                  } catch (error) {
                    setNewsletterSuccess(`❌ Error: ${error.response?.data?.detail || 'Failed to send'}`);
                  } finally {
                    setSendingNewsletter(false);
                  }
                }}
                className="flex-1 py-3 bg-black text-white hover:bg-neutral-800 flex items-center justify-center gap-2"
              >
                <Send size={16} />
                Send Newsletter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
